/**
 * Leitura do extrato do Secullum, casamento de nome e prévia da importação.
 *
 * Papel: aqui ficam `linhasDoPdf` e `linhasDaPlanilha`, que tiram do extrato
 * — em PDF ou nos dois formatos de planilha — o nome, o mês e os três tempos de
 * cada linha; `casarColaborador`, que acha a pessoa da lista do banco de horas
 * pelo nome do extrato; e `montarPrevia`, que decide o que é novo, o que muda e
 * o que fica como está.
 *
 * Como usar: são funções puras sobre o que `extrairItensDoPdf` e
 * `lerCelulasDaPlanilha` devolvem; o arquivo nunca sai do navegador, e a API só
 * recebe `Previa.aEnviar`. As regras são as da origem, uma a uma, inclusive as
 * estranhas — mudar uma delas faz um extrato que a origem lia passar a ser lido
 * de outro jeito:
 *
 * O PDF agrupa em linha os pedaços a até 4 de altura do primeiro da linha e os
 * junta **na ordem da ordenação** (altura e depois `x`), sem reordenar por `x`
 * dentro da linha. O texto das linhas é unido por quebra de linha antes de a
 * expressão do período rodar, e o `\s+` dela atravessa a quebra.
 *
 * Na planilha "Fechamento", o mês é o **primeiro** grupo de dois dígitos da
 * data (o arquivo escreve `MM/DD/AAAA`), e a célula numérica é a hora como
 * fração do dia. Na exportação de aba única, os três tempos são lidos só de
 * texto e entram em valor absoluto.
 *
 * O casamento procura em todos os colaboradores, ativos ou não.
 */
import { type ColaboradorBancoHoras, type LinhaImportadaBancoHoras } from '@concierge/shared';

import { type ItemDeTexto } from '@/shared/lib/pdf';
import {
  type CelulaLida,
  type CelulasLidas,
  type IntervaloDaAba,
} from '@/shared/lib/planilha';
import { lerHoras } from './banco-horas';

/** Uma linha do extrato: de quem, de que mês e os três tempos em minutos. */
export interface LinhaDoExtrato {
  /** O nome como o extrato o escreve. */
  nome: string;
  /** `AAAA-MM`. */
  competencia: string;
  creditoMin: number;
  debitoMin: number;
  ajusteMin: number;
}

/** A distância de altura até a qual um pedaço do PDF fica na linha do primeiro. */
const TOLERANCIA_DA_LINHA = 4;

/** A letra maiúscula, com acento, com que o nome do extrato começa. */
const MAIUSCULA = 'A-ZÁÉÍÓÚÀÈÌÒÙÂÊÎÔÛÃÕÇ';

/** Uma linha da tabela do PDF: período, saldo anterior, crédito, débito e ajuste. */
const LINHA_DO_PDF =
  /(\d{2})\/(\d{2})\/(\d{4})\s+até\s+\d{2}\/\d{2}\/\d{4}\s+(-?\d+:\d{2})\s+(\d+:\d{2})\s+(\d+:\d{2})\s+(\d+:\d{2})/g;

/** O intervalo que a origem supõe quando a aba "Fechamento" não declara um. */
const INTERVALO_DO_FECHAMENTO: IntervaloDaAba = {
  linhaInicial: 0,
  linhaFinal: 99,
  colunaInicial: 0,
  colunaFinal: 25,
};

/** O intervalo que a origem supõe quando a aba única não declara um. */
const INTERVALO_DA_ABA_UNICA: IntervaloDaAba = {
  linhaInicial: 0,
  linhaFinal: 399,
  colunaInicial: 0,
  colunaFinal: 31,
};

/**
 * Monta as linhas de uma página do PDF, como a origem.
 *
 * @param itens - Os pedaços com texto da página.
 * @returns As linhas, cada uma com os pedaços unidos por espaço.
 */
function linhasDaPagina(itens: readonly ItemDeTexto[]): string[] {
  const ordenados = itens
    .filter((item) => item.texto.trim() !== '')
    .map((item) => ({ ...item, texto: item.texto.trim() }))
    .sort((a, b) => b.y - a.y || a.x - b.x);

  const linhas: string[] = [];
  let atual: string[] = [];
  let alturaDaLinha: number | null = null;
  for (const item of ordenados) {
    if (alturaDaLinha === null || Math.abs(item.y - alturaDaLinha) > TOLERANCIA_DA_LINHA) {
      if (atual.length > 0) linhas.push(atual.join(' '));
      atual = [item.texto];
      alturaDaLinha = item.y;
    } else {
      atual.push(item.texto);
    }
  }
  if (atual.length > 0) linhas.push(atual.join(' '));
  return linhas;
}

/**
 * Acha o nome da pessoa numa página do PDF.
 *
 * @param linhas - As linhas da página.
 * @returns O nome, ou vazio quando a página não tem `NOME:` com um nome perto.
 */
function nomeDaPagina(linhas: readonly string[]): string {
  const indice = linhas.findIndex((linha) => /NOME:/i.test(linha));
  if (indice < 0) return '';

  const depoisDoRotulo = (linhas[indice] ?? '')
    .replace(/.*?NOME:\s*/i, '')
    .replace(/\s*Nº\s+FOLHA:.*/i, '')
    .replace(/\s+\d+(\s+\d+)?\s*$/, '')
    .trim();
  if (depoisDoRotulo !== '' && depoisDoRotulo.split(/\s+/).length >= 2) return depoisDoRotulo;

  for (let posicao = indice + 1; posicao < Math.min(indice + 4, linhas.length); posicao += 1) {
    const semNumeros = (linhas[posicao] ?? '')
      .trim()
      .replace(/^\s*[\d\s.]+/, '')
      .trim();
    if (semNumeros === '') continue;
    if (
      new RegExp(`^[${MAIUSCULA}]`).test(semNumeros) &&
      !semNumeros.includes(':') &&
      semNumeros.split(/\s+/).length >= 2
    ) {
      return semNumeros
        .replace(/\s+\d+\s+\d+.*$/, '')
        .replace(/\s+\d+\s*$/, '')
        .trim();
    }
  }
  return '';
}

/**
 * Lê as linhas do extrato em PDF.
 *
 * @param paginas - Os pedaços de cada página, como `extrairItensDoPdf` os devolve.
 * @returns Uma linha por período com algum tempo diferente de zero, de todas
 *   as páginas que têm nome.
 */
export function linhasDoPdf(paginas: readonly (readonly ItemDeTexto[])[]): LinhaDoExtrato[] {
  const resultado: LinhaDoExtrato[] = [];
  for (const itens of paginas) {
    const linhas = linhasDaPagina(itens);
    const nome = nomeDaPagina(linhas);
    if (nome === '') continue;

    for (const achado of linhas.join('\n').matchAll(LINHA_DO_PDF)) {
      const creditoMin = lerHoras(achado[5] ?? '');
      const debitoMin = lerHoras(achado[6] ?? '');
      const ajusteMin = lerHoras(achado[7] ?? '');
      if (creditoMin === 0 && debitoMin === 0 && ajusteMin === 0) continue;
      resultado.push({
        nome,
        competencia: `${achado[3] ?? ''}-${achado[2] ?? ''}`,
        creditoMin,
        debitoMin,
        ajusteMin,
      });
    }
  }
  return resultado;
}

/**
 * O texto de uma célula, quando ela é texto.
 *
 * @param celula - A célula lida, ou `null`.
 * @returns O texto, ou `null` quando a célula falta ou não é texto.
 */
function textoDa(celula: CelulaLida | null): string | null {
  return celula !== null && typeof celula.valor === 'string' ? celula.valor : null;
}

/**
 * Os minutos de uma célula do "Fechamento": texto por `lerHoras`, número como
 * fração do dia.
 *
 * @param celula - A célula lida, ou `null`.
 * @returns Os minutos, ou zero quando a célula falta ou não é texto nem número.
 */
function minutosDoFechamento(celula: CelulaLida | null): number {
  if (celula === null) return 0;
  if (celula.tipo === 's')
    return lerHoras(typeof celula.valor === 'string' ? celula.valor : '');
  return typeof celula.valor === 'number' ? Math.round(celula.valor * 1440) : 0;
}

/**
 * Os minutos de uma célula de texto da aba única, como `parseTimeStr` da origem.
 *
 * Só o texto conta, sem vírgula como separador; a parte ilegível vale zero.
 *
 * @param valor - O valor cru da célula.
 * @returns Os minutos com o sinal do texto.
 */
function minutosDaAbaUnica(valor: unknown): number {
  if (typeof valor !== 'string' || valor === '') return 0;
  const negativo = valor.startsWith('-');
  const [horas = 0, minutos = 0] = valor.replace('-', '').split(':').map(Number);
  const total = (Number.isNaN(horas) ? 0 : horas) * 60 + (Number.isNaN(minutos) ? 0 : minutos);
  return negativo ? -total : total;
}

/**
 * Lê as linhas da planilha do extrato, nos dois formatos da origem.
 *
 * Com alguma aba começando por `Fechamento`, lê todas elas; sem nenhuma, lê a
 * primeira aba como a exportação de aba única do Secullum.
 *
 * @param planilha - A planilha aberta por `lerCelulasDaPlanilha`.
 * @returns Uma linha por período com algum tempo diferente de zero.
 */
export function linhasDaPlanilha(planilha: CelulasLidas): LinhaDoExtrato[] {
  const resultado: LinhaDoExtrato[] = [];
  const fechamentos = planilha.abas.filter((aba) => aba.startsWith('Fechamento'));

  if (fechamentos.length > 0) {
    for (const aba of fechamentos) {
      let nome = '';
      for (let linha = 10; linha <= 14; linha += 1) {
        const texto = textoDa(planilha.celula(aba, linha, 0));
        if (
          texto !== null &&
          new RegExp(`^[${MAIUSCULA}]{2}`).test(texto.trim()) &&
          texto.split(' ').length >= 2
        ) {
          nome = texto.trim();
          break;
        }
      }
      if (nome === '') nome = aba.replace('Fechamento ', '');

      const intervalo = planilha.intervalo(aba) ?? INTERVALO_DO_FECHAMENTO;
      for (let linha = intervalo.linhaInicial; linha <= intervalo.linhaFinal; linha += 1) {
        const texto = textoDa(planilha.celula(aba, linha, 0));
        if (texto === null) continue;
        const achado = /^(\d{2})\/\d{2}\/(\d{4})\s+até/.exec(texto.trim());
        if (achado === null) continue;

        const credito = planilha.celula(aba, linha, 14);
        const debito = planilha.celula(aba, linha, 20);
        if (credito === null && debito === null) continue;
        const creditoMin = minutosDoFechamento(credito);
        const debitoMin = minutosDoFechamento(debito);
        if (creditoMin === 0 && debitoMin === 0) continue;

        resultado.push({
          nome,
          competencia: `${achado[2] ?? ''}-${achado[1] ?? ''}`,
          creditoMin,
          debitoMin,
          ajusteMin: 0,
        });
      }
    }
    return resultado;
  }

  const aba = planilha.abas[0];
  if (aba === undefined) return resultado;
  const intervalo = planilha.intervalo(aba) ?? INTERVALO_DA_ABA_UNICA;
  let nome = '';
  for (let linha = intervalo.linhaInicial; linha <= intervalo.linhaFinal; linha += 1) {
    const texto = textoDa(planilha.celula(aba, linha, 0));
    if (texto === null) continue;
    const valor = texto.trim();

    if (valor === 'NOME:') {
      const abaixo = textoDa(planilha.celula(aba, linha + 1, 0));
      if (abaixo !== null) nome = abaixo.trim();
      continue;
    }

    const achado = /^(\d{2})\/(\d{2})\/(\d{4})\s+até/.exec(valor);
    if (achado === null || nome === '') continue;

    const creditoMin = Math.abs(minutosDaAbaUnica(planilha.celula(aba, linha, 14)?.valor));
    const debitoMin = Math.abs(minutosDaAbaUnica(planilha.celula(aba, linha, 20)?.valor));
    const ajusteMin = Math.abs(minutosDaAbaUnica(planilha.celula(aba, linha, 25)?.valor));
    if (creditoMin === 0 && debitoMin === 0 && ajusteMin === 0) continue;

    resultado.push({
      nome,
      competencia: `${achado[3] ?? ''}-${achado[2] ?? ''}`,
      creditoMin,
      debitoMin,
      ajusteMin,
    });
  }
  return resultado;
}

/** As palavras curtas que não contam no casamento por semelhança. */
const CONECTIVOS = ['da', 'de', 'do', 'dos', 'das', 'e'];

/**
 * Reduz um nome à forma de comparação.
 *
 * @param nome - O nome como está escrito.
 * @returns Em minúsculas, sem acento, só letras e um espaço entre as palavras.
 */
export function normalizarNome(nome: string): string {
  return nome
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z\s]/g, '')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * As palavras de um nome normalizado que contam no casamento.
 *
 * @param nome - O nome já normalizado.
 * @returns As palavras de três letras ou mais que não são conectivo.
 */
function palavrasQueContam(nome: string): string[] {
  return nome
    .split(' ')
    .filter((palavra) => palavra.length >= 3 && !CONECTIVOS.includes(palavra));
}

/**
 * Acha o colaborador de um nome do extrato.
 *
 * Casa pelo nome igual depois de `normalizarNome`, ou por pelo menos duas
 * palavras em comum que cubram 80% da lista menor de palavras.
 *
 * @param nome - O nome como o extrato o escreve.
 * @param colaboradores - Toda a lista do banco de horas, ativos ou não.
 * @returns O primeiro que casar, ou `undefined`.
 */
export function casarColaborador<T extends { nomeCompleto: string }>(
  nome: string,
  colaboradores: readonly T[],
): T | undefined {
  const doExtrato = normalizarNome(nome);
  const palavrasDoExtrato = palavrasQueContam(doExtrato);

  return colaboradores.find((colaborador) => {
    const cadastrado = normalizarNome(colaborador.nomeCompleto);
    if (doExtrato === cadastrado) return true;
    const palavrasDoCadastro = palavrasQueContam(cadastrado);
    const emComum = palavrasDoExtrato.filter((palavra) =>
      palavrasDoCadastro.includes(palavra),
    ).length;
    const menor = Math.min(palavrasDoExtrato.length, palavrasDoCadastro.length);
    return emComum >= 2 && emComum / menor >= 0.8;
  });
}

/** O que a importação faz com uma linha. */
export type AcaoDaImportacao = 'novo' | 'atualizar' | 'nao-encontrado';

/** Uma linha da tabela da prévia. */
export interface LinhaDaPrevia {
  linha: LinhaDoExtrato;
  colaborador: ColaboradorBancoHoras | null;
  acao: AcaoDaImportacao;
}

/** A prévia inteira: a tabela, as quatro contagens e o que "Aplicar" envia. */
export interface Previa {
  /** As linhas com novidade ou sem par, na ordem do extrato. */
  linhas: LinhaDaPrevia[];
  novos: number;
  atualizar: number;
  naoEncontrados: number;
  /** As linhas iguais ao que já está gravado, que não entram na tabela. */
  semAlteracao: number;
  /** Uma linha por pessoa e mês, a última vencendo. */
  aEnviar: LinhaImportadaBancoHoras[];
}

/**
 * Decide o que cada linha do extrato faz.
 *
 * A última linha de cada par nome + mês vence, na posição da primeira. Sem
 * colaborador, `nao-encontrado`; com o mês gravado igual nos três tempos, conta
 * como sem alteração e sai da tabela; gravado com outro valor, `atualizar`;
 * sem o mês, `novo`. Dois nomes do extrato que casam com a mesma pessoa no
 * mesmo mês aparecem os dois na tabela, e só o último é enviado.
 *
 * @param linhas - As linhas lidas do extrato.
 * @param colaboradores - Toda a lista do banco de horas, com os meses.
 * @returns A prévia.
 */
export function montarPrevia(
  linhas: readonly LinhaDoExtrato[],
  colaboradores: readonly ColaboradorBancoHoras[],
): Previa {
  const unicas = new Map<string, LinhaDoExtrato>();
  for (const linha of linhas) unicas.set(`${linha.nome}|${linha.competencia}`, linha);

  const previa: Previa = {
    linhas: [],
    novos: 0,
    atualizar: 0,
    naoEncontrados: 0,
    semAlteracao: 0,
    aEnviar: [],
  };
  const aEnviar = new Map<string, LinhaImportadaBancoHoras>();

  for (const linha of unicas.values()) {
    const colaborador = casarColaborador(linha.nome, colaboradores) ?? null;
    if (colaborador === null) {
      previa.naoEncontrados += 1;
      previa.linhas.push({ linha, colaborador, acao: 'nao-encontrado' });
      continue;
    }

    const gravado = colaborador.meses.find((mes) => mes.competencia === linha.competencia);
    if (
      gravado !== undefined &&
      gravado.creditoMin === linha.creditoMin &&
      gravado.debitoMin === linha.debitoMin &&
      gravado.ajusteMin === linha.ajusteMin
    ) {
      previa.semAlteracao += 1;
      continue;
    }

    const acao: AcaoDaImportacao = gravado === undefined ? 'novo' : 'atualizar';
    if (acao === 'novo') previa.novos += 1;
    else previa.atualizar += 1;
    previa.linhas.push({ linha, colaborador, acao });
    aEnviar.set(`${colaborador.id}|${linha.competencia}`, {
      colaboradorId: colaborador.id,
      competencia: linha.competencia,
      creditoMin: linha.creditoMin,
      debitoMin: linha.debitoMin,
      ajusteMin: linha.ajusteMin,
    });
  }

  previa.aEnviar = [...aEnviar.values()];
  return previa;
}
